import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, BookOpen, Gift, Calendar, Heart, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { submitLead } from "@/lib/api";

const ANGELLA_IMG = "https://customer-assets.emergentagent.com/job_reveal-ready/artifacts/t0vntz6b_Bestselling%20Author%20Angella%20Watkis%20Francis.png";
const BOOK_3D = "https://customer-assets.emergentagent.com/job_reveal-ready/artifacts/0dfwyplo_Book%203D%20POWER%20OF%20MARRSING.png";
const BETTER_WAY = "https://customer-assets.emergentagent.com/job_dc79c178-2c6a-4f41-afbc-479359c635b4/artifacts/ouyw88s8_Founder%20-%20Angella%20Watkis%20Marrsing%20Marrsingclause.png";
const AMAZON_URL = "https://www.amazon.com/dp/B0C5RZF56D";
const APP_URL = "https://app.marrsinglife.com";

export default function About() {
  return (
    <div style={{ overflowX: "hidden" }}>
      <Nav variant="light" />
      <AboutHero />
      <CoreBelief />
      <Author />
      <Invitation />
      <BookSection />
      <StayConnected />
      <Footer />
    </div>
  );
}

/* ────────────────────────────── HERO ────────────────────────────── */
function AboutHero() {
  return (
    <section data-testid="about-hero" style={{
      position: "relative",
      paddingTop: 140, paddingBottom: 88,
      padding: "140px 32px 88px",
      background: `
        radial-gradient(circle at 78% 16%, rgba(111,199,242,0.30), transparent 38%),
        radial-gradient(circle at 10% 80%, rgba(215,181,109,0.16), transparent 40%),
        linear-gradient(180deg, #F8FCFF 0%, #FAF8F3 100%)
      `,
      overflow: "hidden",
    }} className="mc-grain">
      <div style={{
        maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2,
        display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 64, alignItems: "center",
      }} className="mc-two-col">
        <div>
          <div className="mc-fade-up-1" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
            <span style={{ width: 44, height: 1, background: "var(--mc-blue)" }} />
            <span className="mc-eyebrow">About Marrsing®</span>
          </div>
          <h1 className="mc-h1 mc-fade-up-2" style={{ fontSize: "clamp(40px, 6vw, 70px)", marginBottom: 22 }}>
            The Power of the<br /><em>Single Mind</em> in<br />Committed Relationships.
          </h1>
          <p className="mc-fade-up-3 font-serif" style={{
            fontSize: 22, fontStyle: "italic", lineHeight: 1.55,
            color: "var(--mc-text-2)", maxWidth: 560, marginBottom: 32,
          }}>
            "Marrsing® was born from a single question…<br />
            What if the quality of our relationships is not determined by circumstance… but by consciousness?"
          </p>
          <div className="mc-fade-up-4" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href="#stay-connected" className="mc-btn-primary" data-testid="about-hero-stay-cta">
              Stay Connected <ArrowRight size={16} />
            </a>
            <Link to="/quiz" className="mc-btn-secondary" data-testid="about-hero-quiz-cta">
              Take the Clarity Quiz
            </Link>
          </div>
        </div>

        <div className="mc-fade-up-3" style={{ position: "relative" }}>
          <img
            src={ANGELLA_IMG}
            alt="Angella Watkis Francis"
            data-testid="about-author-photo"
            style={{
              width: "100%", maxWidth: 480,
              borderRadius: 28, display: "block", margin: "0 auto",
              boxShadow: "0 32px 70px rgba(31,117,168,0.22)",
              border: "6px solid white",
              objectFit: "cover",
            }}
          />
          <div style={{
            position: "absolute", bottom: -20, left: -10, right: -10,
            display: "flex", justifyContent: "center", pointerEvents: "none",
          }}>
            <div style={{
              background: "rgba(255,255,255,0.92)",
              border: "1px solid var(--mc-border-blue)",
              borderRadius: 20, padding: "12px 22px",
              boxShadow: "0 18px 36px rgba(14,10,31,0.12)",
              fontFamily: "var(--font-mono)", fontSize: 11,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "var(--mc-blue-deep)", fontWeight: 600,
            }}>
              Bestselling Author · Visionary Founder
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 920px) {
          .mc-two-col { grid-template-columns: 1fr !important; gap: 56px !important; }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────── CORE BELIEF ────────────────────────────── */
function CoreBelief() {
  return (
    <section data-testid="about-core-belief" style={{ padding: "104px 32px", background: "transparent" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 22, justifyContent: "center" }}>
          <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
          <span className="mc-eyebrow">At its Core</span>
          <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
        </div>
        <h2 className="mc-h2" style={{ fontSize: "clamp(30px, 4vw, 46px)", marginBottom: 32 }}>
          Before there is <em>"us,"</em><br />there is the <em>single mind.</em>
        </h2>
        <p style={{ fontSize: 17.5, lineHeight: 1.8, color: "var(--mc-text-2)", marginBottom: 22 }}>
          At its core, <strong>Marrsing®</strong> is the disciplined understanding that every committed relationship and marriage is shaped first by the individual self — by our thoughts, perspectives, emotional responses, and intentional choices.
        </p>

        <div style={{
          margin: "44px auto",
          padding: "32px 28px",
          background: "rgba(255,255,255,0.78)",
          border: "1px solid var(--mc-border-blue)",
          borderRadius: 24,
          boxShadow: "0 18px 44px rgba(14,10,31,0.06)",
          maxWidth: 640,
        }}>
          <ul className="font-serif" style={{
            listStyle: "none", padding: 0, margin: 0,
            fontSize: "clamp(20px, 2.4vw, 26px)",
            lineHeight: 1.6, color: "var(--mc-text)", fontWeight: 400,
          }}>
            <li style={{ padding: "10px 0", borderBottom: "1px solid var(--mc-border)" }}>
              Before there is "us," there is the <em style={{ color: "var(--mc-blue)" }}>single mind.</em>
            </li>
            <li style={{ padding: "10px 0", borderBottom: "1px solid var(--mc-border)" }}>
              Before there is harmony, there is <em style={{ color: "var(--mc-blue)" }}>awareness.</em>
            </li>
            <li style={{ padding: "10px 0" }}>
              Before there is transformation, there is <em style={{ color: "var(--mc-blue)" }}>a pause.</em>
            </li>
          </ul>
        </div>

        <p className="font-serif" style={{
          fontSize: 22, fontStyle: "italic", color: "var(--mc-blue-deep)", lineHeight: 1.5,
        }}>
          Marrsing® is the practice of choosing that pause.
        </p>
      </div>
    </section>
  );
}

/* ────────────────────────────── AUTHOR ────────────────────────────── */
function Author() {
  return (
    <section data-testid="about-author-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(180deg, var(--mc-blue-pale) 0%, white 100%)",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr)", gap: 56, alignItems: "start" }} className="mc-two-col">
          <div>
            <div style={{
              position: "sticky", top: 100,
              background: "rgba(255,255,255,0.85)",
              border: "1px solid var(--mc-border-blue)",
              borderRadius: 24,
              padding: "32px 28px",
              textAlign: "center",
              boxShadow: "0 18px 44px rgba(14,10,31,0.06)",
            }}>
              <img
                src={BETTER_WAY}
                alt="A Better Way to Marrsing"
                style={{ width: "100%", borderRadius: 16, marginBottom: 18 }}
              />
              <div className="font-mono" style={{
                fontSize: 10.5, letterSpacing: "0.14em",
                textTransform: "uppercase", color: "var(--mc-blue-deep)",
                fontWeight: 600, marginBottom: 8,
              }}>Creator of MarrsingClause®</div>
              <div className="font-serif" style={{
                fontSize: 26, fontWeight: 500, color: "var(--mc-text)", marginBottom: 4,
              }}>Angella Watkis Francis</div>
              <p style={{ fontSize: 13, color: "var(--mc-text-3)", lineHeight: 1.6 }}>
                Bestselling Author · Relationship Architect · Global Thought Leader
              </p>
            </div>
          </div>

          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
              <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
              <span className="mc-eyebrow">Bestselling Author · Visionary Founder</span>
            </div>
            <h2 className="mc-h2" style={{ fontSize: "clamp(28px, 3.8vw, 42px)", marginBottom: 28 }}>
              A journey of lived <em>devotion</em>, growth, and transformation.
            </h2>
            <div style={{ fontSize: 16, lineHeight: 1.85, color: "var(--mc-text-2)" }}>
              <p>
                Angella Watkis Francis is the author of <em>"The Power of Marrsing: Tapping Into Our Individual Consciousness,"</em> and the visionary architect behind the Marrsing® philosophy and the MarrsingClause® commitment framework.
              </p>
              <p style={{ marginTop: 18 }}>
                Her life story is not one of theory — it is one of lived devotion, growth, reflection, and transformation. After experiencing young love at the tender age of thirteen, she later married her childhood sweetheart and spent decades cultivating a marriage rooted in commitment, shared history, and evolving partnership. Through the seasons of love, learning, challenge, and maturity, she built a family and started a legacy; raising three children of her own after embracing another son within the expanding story of her life.
              </p>
              <p style={{ marginTop: 18 }}>
                From that journey emerged a powerful realization that became the foundation of Marrsing: that every relationship is ultimately shaped not by circumstance, but by consciousness. In <em>The Power of Marrsing — Tapping Into Our Individual Consciousness,</em> she explores how the <strong>"single mind"</strong> drives each individual self within a committed union, and carries the power to strengthen or weaken connections through thoughts, choices, emotional awareness, and responses.
              </p>
              <p style={{ marginTop: 18 }}>
                Her work bridges personal transformation and relational architecture. It integrates emotional intelligence, intentional living, spiritual grounding, and conscious accountability. Through writing, speaking, coaching, and the MarrsingClause® digital platform, Angella continues to guide partners toward deeper awareness, stronger alignment, and more harmonious relationships.
              </p>
              <div className="font-serif" style={{
                marginTop: 32, padding: "24px 28px",
                background: "rgba(255,255,255,0.85)",
                borderLeft: "3px solid var(--mc-gold)",
                borderRadius: 16,
                fontStyle: "italic", fontSize: 19, color: "var(--mc-text)", lineHeight: 1.55,
              }}>
                She does not present Marrsing® as perfection.<br />
                <span style={{ color: "var(--mc-blue-deep)" }}>She presents it as responsibility.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── INVITATION ────────────────────────────── */
function Invitation() {
  const decisions = [
    "A decision to pause before reacting.",
    "A decision to love with intention rather than impulse.",
    "A decision to recognize that the power to transform a relationship begins within the single conscious self.",
  ];
  return (
    <section data-testid="about-invitation" style={{
      padding: "104px 32px",
      background: `
        radial-gradient(circle at 50% 20%, rgba(111,199,242,0.35), transparent 40%),
        linear-gradient(135deg, var(--mc-blue-deep) 0%, var(--mc-blue) 100%)
      `,
      color: "white",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}>
      <div className="font-serif" style={{
        position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
        fontSize: 520, fontWeight: 300, color: "rgba(255,255,255,0.06)",
        pointerEvents: "none", userSelect: "none", lineHeight: 1,
      }}>M</div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 18, justifyContent: "center" }}>
          <span style={{ width: 36, height: 1, background: "rgba(255,255,255,0.75)" }} />
          <span className="font-mono" style={{
            fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "white", fontWeight: 600,
          }}>The Invitation</span>
          <span style={{ width: 36, height: 1, background: "rgba(255,255,255,0.75)" }} />
        </div>

        <h2 className="font-serif" style={{
          fontSize: "clamp(34px, 5vw, 56px)", fontWeight: 300, lineHeight: 1.14,
          color: "white", marginBottom: 28,
        }}>
          Marrsing® is more than a concept.
        </h2>

        <ul className="font-serif" style={{
          listStyle: "none", padding: 0, margin: "0 auto 40px",
          fontSize: "clamp(18px, 2.2vw, 22px)", lineHeight: 1.7,
          color: "rgba(255,255,255,0.85)", maxWidth: 520,
        }}>
          <li>It is more than a book.</li>
          <li>It is more than an app.</li>
          <li style={{
            color: "var(--mc-blue-pale)",
            fontStyle: "italic", marginTop: 14, fontSize: "1.1em",
          }}>It is a conscious decision.</li>
        </ul>

        <div style={{
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.16)",
          backdropFilter: "blur(10px)",
          borderRadius: 22,
          padding: "32px 28px",
          marginBottom: 36,
        }}>
          {decisions.map((d, i) => (
            <p key={i} className="font-serif" style={{
              fontSize: "clamp(17px, 2vw, 20px)",
              lineHeight: 1.6,
              color: "white",
              padding: "10px 0",
              borderBottom: i < decisions.length - 1 ? "1px solid rgba(255,255,255,0.12)" : "none",
              fontStyle: "italic",
            }}>{d}</p>
          ))}
        </div>

        <p style={{
          fontSize: 17, color: "rgba(255,255,255,0.92)",
          maxWidth: 560, margin: "0 auto 16px", lineHeight: 1.75,
        }}>
          Marrsing® invites committed partners to choose <strong>awareness over assumption</strong>, <strong>growth over ego</strong>, and <strong>harmony over habit</strong>.
        </p>

        <p className="font-serif" style={{
          fontSize: "clamp(22px, 2.8vw, 30px)", fontStyle: "italic",
          color: "var(--mc-blue-pale)", lineHeight: 1.4, marginTop: 24,
        }}>
          Because transformation does not begin with two people.<br />
          It begins with one.<br />
          And then another.<br />
          <span style={{ color: "white", fontWeight: 500 }}>Together.</span>
        </p>
      </div>
    </section>
  );
}

/* ────────────────────────────── BOOK ────────────────────────────── */
function BookSection() {
  return (
    <section data-testid="about-book-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(180deg, var(--mc-cream) 0%, white 100%)",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 64, alignItems: "center",
        }} className="mc-two-col">
          <div style={{ textAlign: "center" }}>
            <img
              src={BOOK_3D}
              alt="The Power of Marrsing — book cover"
              data-testid="about-book-image"
              style={{
                maxWidth: 380, width: "100%",
                filter: "drop-shadow(0 30px 60px rgba(14,10,31,0.22))",
              }}
              className="mc-float"
            />
          </div>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
              <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
              <span className="mc-eyebrow">The Book That Started It All</span>
            </div>
            <h2 className="mc-h2" style={{ fontSize: "clamp(30px, 4vw, 46px)", marginBottom: 16 }}>
              The Power of <em>Marrsing.</em>
            </h2>
            <p style={{ fontSize: 16, color: "var(--mc-text-3)", lineHeight: 1.7, marginBottom: 24, fontStyle: "italic" }}>
              Tapping Into Our Individual Consciousness
            </p>
            <p style={{ fontSize: 16, color: "var(--mc-text-2)", lineHeight: 1.8, marginBottom: 28 }}>
              Understanding how our purpose, power, and passion within the individual self influences and defines our commitment in our relationships and marriages.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href={AMAZON_URL} target="_blank" rel="noreferrer" className="mc-btn-primary" data-testid="about-book-cta">
                Get the Book <ExternalLink size={14} />
              </a>
              <a href={APP_URL} target="_blank" rel="noreferrer" className="mc-btn-secondary" data-testid="about-app-cta">
                Enter the MarrsingClause® App
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── STAY CONNECTED FORM ────────────────────────────── */
function StayConnected() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", anniversary_date: "", relationship_status: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.first_name) {
      toast.error("Please enter your first name.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        source: "stay-connected",
        extra: {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          address: form.address,
          anniversary_date: form.anniversary_date,
          relationship_status: form.relationship_status,
        },
      });
      setDone(true);
      toast.success("You're on the list — welcome to the Marrsing® family.");
    } catch (err) {
      toast.error("Could not save right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="stay-connected" data-testid="stay-connected-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(180deg, white 0%, var(--mc-blue-pale) 100%)",
    }}>
      <div style={{
        maxWidth: 760, margin: "0 auto",
        background: "rgba(255,255,255,0.94)",
        border: "1px solid var(--mc-border-blue)",
        borderRadius: 32,
        padding: "56px 44px",
        boxShadow: "0 28px 70px rgba(31,117,168,0.14)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <Gift size={18} color="var(--mc-gold)" />
            <span className="mc-eyebrow">Stay Connected</span>
            <Sparkles size={18} color="var(--mc-blue)" />
          </div>
          <h2 className="mc-h2" style={{ fontSize: "clamp(28px, 3.6vw, 42px)", marginBottom: 14 }}>
            Join the <em>Marrsing® family.</em>
          </h2>
          <p style={{ fontSize: 16, color: "var(--mc-text-2)", lineHeight: 1.7, maxWidth: 540, margin: "0 auto" }}>
            Stay connected and be added to the <strong>Marrsing® gifts, prizes, and surprises</strong>. Anniversaries celebrated. Milestones honored. Wisdom shared.
          </p>
        </div>

        {done ? (
          <div style={{
            textAlign: "center", padding: "32px 24px",
            background: "rgba(127,205,187,0.16)",
            border: "1px solid rgba(127,205,187,0.45)",
            borderRadius: 22,
          }} data-testid="stay-connected-success">
            <Heart size={28} color="var(--mc-teal)" style={{ marginBottom: 12 }} />
            <h3 className="font-serif" style={{ fontSize: 26, color: "var(--mc-text)", marginBottom: 8 }}>
              Welcome to the family.
            </h3>
            <p style={{ fontSize: 15, color: "var(--mc-text-2)", lineHeight: 1.65 }}>
              You're on the list. Look for surprises, gifts, and stories from Angella in your inbox.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="stay-connected-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="mc-form-row">
              <Field label="First name *" testid="sc-first-name">
                <input data-testid="sc-first-name" required type="text" value={form.first_name} onChange={set("first_name")} placeholder="Angella" style={inputStyle} />
              </Field>
              <Field label="Last name" testid="sc-last-name">
                <input data-testid="sc-last-name" type="text" value={form.last_name} onChange={set("last_name")} placeholder="Watkis Francis" style={inputStyle} />
              </Field>
            </div>

            <Field label="Email address *">
              <input data-testid="sc-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" style={inputStyle} />
            </Field>

            <Field label="Phone number">
              <input data-testid="sc-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" style={inputStyle} />
            </Field>

            <Field label="Mailing address">
              <textarea data-testid="sc-address" value={form.address} onChange={set("address")} placeholder="Street, City, State, ZIP" rows={2} style={{ ...inputStyle, minHeight: 70, resize: "vertical", paddingTop: 14 }} />
            </Field>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="mc-form-row">
              <Field label="Anniversary date" icon={<Calendar size={14} />}>
                <input data-testid="sc-anniversary" type="date" value={form.anniversary_date} onChange={set("anniversary_date")} style={inputStyle} />
              </Field>
              <Field label="Relationship status">
                <select data-testid="sc-status" value={form.relationship_status} onChange={set("relationship_status")} style={inputStyle}>
                  <option value="">Select...</option>
                  <option value="Met">Recently Met</option>
                  <option value="Dating">Dating</option>
                  <option value="Engaged">Engaged</option>
                  <option value="Married">Married</option>
                  <option value="Long-term">Long-term Partnership</option>
                </select>
              </Field>
            </div>

            <button data-testid="sc-submit" type="submit" disabled={submitting} className="mc-btn-primary" style={{ width: "100%", marginTop: 8 }}>
              {submitting ? "Sending…" : "Stay Connected"} {!submitting && <ArrowRight size={16} />}
            </button>
            <p className="font-mono" style={{ marginTop: 6, fontSize: 11.5, color: "var(--mc-text-3)", letterSpacing: "0.04em", textAlign: "center", lineHeight: 1.6 }}>
              Private &amp; confidential. We never share your data. Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .mc-form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, icon }) {
  return (
    <div>
      <label className="font-mono" style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 500, color: "var(--mc-text-3)",
        marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase",
      }}>
        {icon}{label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "13px 16px",
  borderRadius: 12,
  border: "1px solid var(--mc-border)",
  background: "white",
  fontFamily: "var(--font-sans)",
  fontSize: 15,
  color: "var(--mc-text)",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  minHeight: 50,
};
